import { BUSINESS_FEATURE_FLAGS, flagBusinessId } from '@mondaygpt/constants'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { posthogClient } from 'services/posthog/posthog.service'
import { selectBusinessId, selectCurrentUserId } from '../common'
import { useBusinessFlagsStore } from './business-flags-store'

export { useFeatureGate, useFeatureGateState } from './use-feature-gate'
export { usePlatformRolloutEnabled } from './use-platform-rollout'

const HAS_BUSINESS_FLAGS = BUSINESS_FEATURE_FLAGS.length > 0

/**
 * Loads PostHog feature flags for the logged-in identity. posthog-js skips the
 * anonymous first-load request (`advanced_disable_feature_flags_on_first_load`),
 * so the only `/flags` request in the app is the one made here once the user is
 * known — no anonymous defaults can ever be mistaken for the user's flags.
 * `identify` is idempotent; `reloadFeatureFlags` also covers a refresh where
 * the persisted distinct id already equals the user (identify would not reload).
 *
 * Business-scoped flags load from our server for the session's business, again
 * on every business switch (see `business-flags-store`). Skipped while the
 * registry has no business flags.
 */
export const FeatureFlagProvider = ({ children }: { children: React.ReactNode }) => {
  const currentUserId = useSelector(selectCurrentUserId)
  // The onboarding sentinel is no business: nothing to load, business flags are off
  const currentBusinessId = flagBusinessId(useSelector(selectBusinessId))
  const loadBusinessFlags = useBusinessFlagsStore(state => state.load)
  const resetBusinessFlags = useBusinessFlagsStore(state => state.reset)

  useEffect(() => {
    if (!currentUserId) return
    posthogClient.identify(currentUserId)
    posthogClient.reloadFeatureFlags()
  }, [currentUserId])

  useEffect(() => {
    if (!HAS_BUSINESS_FLAGS) return
    if (!currentUserId || !currentBusinessId) {
      resetBusinessFlags()
      return
    }
    // Effects can't await; handle the promise instead of dropping it. load
    // settles its own API failures, so this only fires on a bug in the store.
    loadBusinessFlags(currentBusinessId).catch(error =>
      console.error('Business feature flags store threw unexpectedly:', error)
    )
  }, [currentUserId, currentBusinessId, loadBusinessFlags, resetBusinessFlags])

  return children
}
